import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Search, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pulsante per cercare foto online (Google CSE) come alternativa/ispirazione
 * alla generazione AI. Risultati mostrati in dialog con link diretto alla fonte.
 */
export default function ImageSearchButton({ productId, defaultQuery = '' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(defaultQuery);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const doSearch = async (q) => {
    setLoading(true);
    setResults([]);
    try {
      const { data } = await axios.post(
        `${API}/tools/image-search`,
        { query: q, product_id: productId, num: 8 },
        { withCredentials: true }
      );
      setResults(data.results || []);
      if (!data.results?.length) toast.message('Nessun risultato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore ricerca immagini');
    } finally {
      setLoading(false);
    }
  };

  const openAndSearch = () => {
    setOpen(true);
    if (productId || query) doSearch(query);
  };

  return (
    <>
      <Button
        data-testid="image-search-btn"
        type="button"
        onClick={openAndSearch}
        variant="outline"
        className="w-full h-10 rounded-full border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/5 text-sm"
      >
        <Search size={14} className="mr-1.5" />
        Cerca foto online
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="image-search-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cerca foto online</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input
              data-testid="image-search-input"
              placeholder="Es: tazza ceramica bianca"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doSearch(query); }}
              className="h-10 rounded-xl border-[#E8E3D9]"
            />
            <Button
              data-testid="image-search-run-btn"
              onClick={() => doSearch(query)}
              disabled={loading || !query.trim()}
              className="h-10 rounded-full bg-[#8B5CF6] hover:bg-[#7C4FE5] text-white px-4"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Cerca'}
            </Button>
          </div>

          {loading && (
            <div className="text-center py-10 text-sm text-[#877E78]">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} /> Ricerca in corso...
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {results.map((r, i) => (
                <a
                  key={i}
                  href={r.context || r.image_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`image-search-result-${i}`}
                  className="block rounded-lg overflow-hidden border border-[#E8E3D9] hover:border-[#8B5CF6] transition group"
                >
                  <div className="aspect-square bg-[#F0EBE1] overflow-hidden relative">
                    <img
                      src={r.thumbnail || r.image_url}
                      alt={r.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                      onError={(e) => { e.target.style.opacity = '0.3'; }}
                    />
                    <div className="absolute bottom-1 right-1 bg-white/80 rounded-full p-1">
                      <ExternalLink size={10} />
                    </div>
                  </div>
                  <div className="p-1.5">
                    <p className="text-[10px] text-[#5C554F] truncate" title={r.title}>{r.title}</p>
                    <p className="text-[9px] text-[#877E78] truncate">{r.source}</p>
                  </div>
                </a>
              ))}
            </div>
          )}

          {!loading && results.length === 0 && !query && (
            <p className="text-center text-sm text-[#877E78] py-8">
              Inserisci una query per cercare foto online.
            </p>
          )}

          <p className="text-[10px] text-[#877E78] mt-3 text-center">
            Risultati da Google Images. Clicca una foto per aprire la fonte originale.
            Verifica sempre i diritti prima di riutilizzare un'immagine.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
